import React from 'react';
import PropTypes from 'prop-types';
import { useEffectOnce } from 'react-use';
import { useDispatch } from 'react-redux';
import { isNumeric, isUuid, createCustomPropType } from '../../../../js/app/react/validators';
import { EDIT_ACCOUNT, DELETE_ACCOUNT } from './AccountsOverviewMenuOptions';
import EmptyState from './EmptyStateContainer';
import Button from '../../../CommonComponents/Button';
import AdvForm from '../../../CommonComponents/AdvForm';
import { BACKEND_KEY } from '../FinancialAccountsWorkflowConstants';
import Back from '../../Navigation/Back/BackContainer';
import { PlaidLinkInfoAlert } from '../../../Plaid/components/PlaidLinkInfoAlert/PlaidLinkInfoAlert';
import { setIsPlaidInfoMessageVisibility } from '../../../Plaid/state/actions';

const AccountsOverviewView = ({
  clientId,
  workflowId,
  accountsList,
  graphAccounts,
  onMenuOptionClicked,
  onAddAccount,
  noDeletedAccounts,
  isEmptyState,
  hasAddAnotherGoal,
  goToAddAnotherGoal,
  takeMeToMyAccounts,
  canGoBack,
  hasAccessToLinkAccounts,
  isLoading,
  hasPlaid,
}) => {
  const dispatch = useDispatch();

  useEffectOnce(() => {
    return () => {
      dispatch(setIsPlaidInfoMessageVisibility(false));
    };
  });

  const onEditClicked = id =>
    onMenuOptionClicked(workflowId, clientId, id, graphAccounts)(EDIT_ACCOUNT);

  const onDeleteClicked = id =>
    onMenuOptionClicked(workflowId, clientId, id, graphAccounts)(DELETE_ACCOUNT);

  if (isLoading) {
    return null;
  }

  if (isEmptyState && noDeletedAccounts) {
    return <EmptyState workflowId={workflowId} />;
  }

  return (
    <div className="AccountsOverviewView">
      <h2 className="h2">Manage your accounts</h2>
      {canGoBack && (
        <Back
          backText="Back"
          workflowId={workflowId}
          validationId={`${BACKEND_KEY}.AccountsLinkingSteps`}
        />
      )}

      <PlaidLinkInfoAlert mt="24" mb="16" />

      <div className="adv-workflow-crud-widget">
        {isEmptyState && !noDeletedAccounts ? (
          <div className="adv-workflow-crud-empty">
            <div className="adv-workflow-crud-empty-add">
              <Button
                className="adv-workflow-crud-empty-add-button"
                type="button"
                onClick={onAddAccount(workflowId, clientId, hasAccessToLinkAccounts, hasPlaid)}
              >
                <i className="adv-workflow-crud-empty-add-button-i">+</i>
                <br />
                Add a new account
              </Button>
            </div>
          </div>
        ) : (
          <div className="adv-workflow-crud-populated">
            <ul className="adv-workflow-crud-populated-ul">
              {/* eslint-disable-next-line */}
              {accountsList.map(({ id, label, value, isLink }) => (
                <li className="adv-workflow-crud-populated-li" key={id}>
                  <div className="adv-workflow-crud-populated-name-2">{label}</div>
                  <div className="adv-workflow-crud-populated-value-2">{value}</div>
                  <div className="adv-workflow-crud-populated-actions">
                    <button
                      type="button"
                      className="adv-workflow-crud-populated-action"
                      aria-label="edit account"
                      onClick={onEditClicked(id)}
                    >
                      <i className="adv-workflow-crud-populated-action-i far fa-edit" />
                    </button>
                    {isLink && (
                      <div className="adv-workflow-crud-populated-action">
                        <i className="adv-workflow-crud-populated-action-i fa fa-link" />
                      </div>
                    )}
                    {!isLink && (
                      <button
                        type="button"
                        className="adv-workflow-crud-populated-action"
                        aria-label="remove account"
                        onClick={onDeleteClicked(id)}
                      >
                        <i className="adv-workflow-crud-populated-action-i far fa-trash-alt" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="adv-workflow-crud-populated-add">
              <button
                className="adv-workflow-crud-populated-add-button"
                onClick={onAddAccount(workflowId, clientId, hasAccessToLinkAccounts, hasPlaid)}
                type="button"
              >
                <i className="adv-workflow-crud-populated-add-button-i">+</i>
                Add a new account
              </button>
            </div>
          </div>
        )}
      </div>
      <AdvForm className="AccountAddEditView">
        <div className="footer">
          <button
            type="button"
            className={
              hasAddAnotherGoal
                ? 'adv-workflow-navigation-cancel-button'
                : 'adv-workflow-navigation-next-save-button'
            }
            onClick={takeMeToMyAccounts(workflowId, clientId)}
          >
            Finish
          </button>

          {hasAddAnotherGoal && (
            <button
              type="button"
              className="adv-workflow-navigation-next-save-button"
              onClick={goToAddAnotherGoal(clientId)}
            >
              Add another goal
            </button>
          )}
        </div>
      </AdvForm>
    </div>
  );
};

AccountsOverviewView.propTypes = {
  clientId: createCustomPropType(isNumeric).isRequired,
  workflowId: PropTypes.string.isRequired,
  accountsList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      id: createCustomPropType(isUuid).isRequired,
      isSelected: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  graphAccounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onMenuOptionClicked: PropTypes.func.isRequired,
  onAddAccount: PropTypes.func.isRequired,
  hasAddAnotherGoal: PropTypes.bool.isRequired,
  goToAddAnotherGoal: PropTypes.func.isRequired,
  noDeletedAccounts: PropTypes.bool.isRequired,
  isEmptyState: PropTypes.bool.isRequired,
  takeMeToMyAccounts: PropTypes.func.isRequired,
  canGoBack: PropTypes.bool.isRequired,
  hasAccessToLinkAccounts: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasPlaid: PropTypes.bool.isRequired,
};

export default AccountsOverviewView;
